import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

interface InfoGridProps {
  children: ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}

/**
 * Grid layout for label/value pairs
 */
export function InfoGrid({ children, columns = 2, className }: InfoGridProps) {
  const colClasses = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4',
  };

  return (
    <div className={cn('grid gap-4', colClasses[columns], className)}>
      {children}
    </div>
  );
}

interface InfoItemProps {
  label: string;
  value: ReactNode;
  className?: string;
  monospace?: boolean;
}

/**
 * Single label/value item for InfoGrid
 */
export function InfoItem({ label, value, className, monospace = false }: InfoItemProps) {
  return (
    <div className={className}>
      <div className="text-xs text-slate-500 font-medium mb-0.5">{label}</div>
      <div className={cn(
        'text-sm text-slate-700 font-medium',
        monospace && 'font-mono'
      )}>
        {value || '—'}
      </div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: ReactNode;
  className?: string;
  monospace?: boolean;
}

/**
 * Horizontal label: value row
 */
export function InfoRow({ label, value, className, monospace = false }: InfoRowProps) {
  return (
    <div className={cn('flex items-center justify-between py-1', className)}>
      <span className="text-sm text-slate-500">{label}</span>
      <span className={cn(
        'text-sm text-slate-700 font-medium',
        monospace && 'font-mono'
      )}>
        {value || '—'}
      </span>
    </div>
  );
}

interface SectionHeaderProps {
  title: string;
  count?: number;
  icon?: ReactNode;
  className?: string;
}

/**
 * Section header with optional count and icon
 */
export function SectionHeader({ title, count, icon, className }: SectionHeaderProps) {
  return (
    <div className={cn('flex items-center gap-2 mb-4', className)}>
      {icon && <span className="text-slate-400">{icon}</span>}
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      {count !== undefined && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
          {count}
        </span>
      )}
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  className?: string;
}

/**
 * Empty state placeholder
 */
export function EmptyState({ title, description, icon, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
      {icon && (
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <span className="text-slate-400">{icon}</span>
        </div>
      )}
      <p className="text-slate-600 font-medium">{title}</p>
      {description && (
        <p className="text-sm text-slate-500 mt-1">{description}</p>
      )}
    </div>
  );
}
